import React, { createContext, useContext, useCallback, useRef } from "react";

const STORAGE_KEY = "memoryFields";

/**
 * MemoryFieldContext
 *
 * Provides get/set helpers for "memory fields" — fields whose last agent-entered
 * value is remembered and auto-prefilled in subsequent forms within the same
 * browser session.
 *
 * Values are keyed by fieldId and persisted in sessionStorage so they survive
 * page refreshes but are cleared when the tab/browser is closed or the agent logs out.
 */
const MemoryFieldContext = createContext(null);

function readStore() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeStore(store) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (e) {
    console.warn("MemoryField: could not write to sessionStorage", e);
  }
}

export const MemoryFieldProvider = ({ children }) => {
  // Use a ref so the store object is always current without causing re-renders
  const storeRef = useRef(readStore());

  const getMemoryValue = useCallback((fieldId) => {
    // Always read from sessionStorage to stay in sync across components
    const store = readStore();
    storeRef.current = store;
    return store[fieldId] ?? null;
  }, []);

  const setMemoryValue = useCallback((fieldId, value) => {
    const store = readStore();
    store[fieldId] = value;
    storeRef.current = store;
    writeStore(store);
  }, []);

  return (
    <MemoryFieldContext.Provider value={{ getMemoryValue, setMemoryValue }}>
      {children}
    </MemoryFieldContext.Provider>
  );
};

/**
 * Hook to access the memory field store.
 * Returns { getMemoryValue, setMemoryValue } or null when used outside the provider
 * (e.g. in the admin preview).
 */
export const useMemoryFields = () => {
  return useContext(MemoryFieldContext);
};

/**
 * Utility to clear all memory field data.
 * Can be called without the React context (e.g. from PostLogout).
 */
export const clearMemoryFields = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // no-op
  }
};
